import { closestCenter, pointerWithin } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import uniq from 'lodash-es/uniq'

import { EMPTY_LIST } from '../../../constants/common'
import { isValueNumberType } from '../../../utils/common'

import {
  BIRDVIEW_LAYOUT_COLUMN,
  BIRDVIEW_LAYOUT_COLUMN_KEYS,
} from './BirdviewLayout.constants'

function checkIsBirdviewColumnId(value) {
  return BIRDVIEW_LAYOUT_COLUMN_KEYS.includes(value)
}

function getBirdviewKeptColumns(source, widgetIds) {
  const allowed = new Set(widgetIds)
  const left = uniq(source.left).filter(id => allowed.has(id))
  const placed = new Set(left)

  return {
    left,
    right: uniq(source.right).filter(id => allowed.has(id) && !placed.has(id)),
  }
}

function getBirdviewDefaultColumnKey(defaultLayout, id) {
  return defaultLayout.right.includes(id)
    ? BIRDVIEW_LAYOUT_COLUMN.right
    : BIRDVIEW_LAYOUT_COLUMN.left
}

// A widget missing from the stored layout goes before its first default
// neighbour, so it appears in place instead of at the end of the column.
function getBirdviewInsertPosition({ defaultColumn, id, targetColumn }) {
  const neighbourPosition = defaultColumn
    .slice(defaultColumn.indexOf(id) + 1)
    .map(neighbourId => targetColumn.indexOf(neighbourId))
    .find(position => position !== -1)

  return neighbourPosition ?? targetColumn.length
}

function getBirdviewColumnWidgetContainers({ column, droppableContainers }) {
  return droppableContainers.filter(
    container => container.data.current?.sortable?.containerId === column
  )
}

function getBirdviewPointerRect({ x, y }) {
  return { bottom: y, height: 0, left: x, right: x, top: y, width: 0 }
}

function getBirdviewPointerCollisions(args) {
  const [collision] = pointerWithin(args)

  if (!collision || !checkIsBirdviewColumnId(collision.id)) {
    return collision ? [collision] : EMPTY_LIST
  }

  const widgetContainers = getBirdviewColumnWidgetContainers({
    column: collision.id,
    droppableContainers: args.droppableContainers,
  })

  return widgetContainers.length > 0
    ? closestCenter({
        ...args,
        collisionRect: getBirdviewPointerRect(args.pointerCoordinates),
        droppableContainers: widgetContainers,
      })
    : [collision]
}

export function checkIsBirdviewLayoutShape(value) {
  return Array.isArray(value?.left) && Array.isArray(value?.right)
}

export function getBirdviewLayoutWidgetIds(layout) {
  return [...layout.left, ...layout.right]
}

export function reconcileBirdviewLayout({ defaultLayout, layout, widgetIds }) {
  const columns = getBirdviewKeptColumns(layout ?? defaultLayout, widgetIds)
  const placed = new Set(getBirdviewLayoutWidgetIds(columns))

  uniq(widgetIds)
    .filter(id => !placed.has(id))
    .forEach(id => {
      const columnKey = getBirdviewDefaultColumnKey(defaultLayout, id)
      const targetColumn = columns[columnKey]
      const insertAt = getBirdviewInsertPosition({
        defaultColumn: defaultLayout[columnKey],
        id,
        targetColumn,
      })

      targetColumn.splice(insertAt, 0, id)
    })

  return columns
}

export function findBirdviewColumn(layout, id) {
  if (layout.left.includes(id)) {
    return BIRDVIEW_LAYOUT_COLUMN.left
  }

  if (layout.right.includes(id)) {
    return BIRDVIEW_LAYOUT_COLUMN.right
  }

  return null
}

export function getBirdviewOverColumn(over) {
  const containerId = over?.data?.current?.sortable?.containerId

  if (checkIsBirdviewColumnId(containerId)) {
    return containerId
  }

  if (checkIsBirdviewColumnId(over?.id)) {
    return over.id
  }

  return null
}

export function getBirdviewOverIndex(over, targetList) {
  if (checkIsBirdviewColumnId(over?.id)) {
    return targetList.length
  }

  const overIndex = over?.data?.current?.sortable?.index

  return isValueNumberType(overIndex) ? overIndex : targetList.length
}

// The pointer picks the target: by its centre a wide widget can sit nearer the
// other column and bounce between the two. The keyboard has no pointer.
export function getBirdviewLayoutCollisions(args) {
  return args.pointerCoordinates
    ? getBirdviewPointerCollisions(args)
    : closestCenter(args)
}

export function getBirdviewLayoutWithMovedWidget({
  activeId,
  layout,
  over,
  overColumn,
}) {
  const activeColumn = findBirdviewColumn(layout, activeId)
  const fromIndex = activeColumn ? layout[activeColumn].indexOf(activeId) : -1

  if (activeColumn === overColumn || fromIndex < 0) {
    return layout
  }

  const next = { left: [...layout.left], right: [...layout.right] }

  next[activeColumn].splice(fromIndex, 1)
  next[overColumn].splice(
    getBirdviewOverIndex(over, next[overColumn]),
    0,
    activeId
  )

  return next
}

export function getBirdviewLayoutWithSortedColumn({
  activeId,
  layout,
  over,
  overColumn,
}) {
  const column = findBirdviewColumn(layout, activeId)

  if (column !== overColumn) {
    return layout
  }

  const list = layout[column]
  const fromIndex = list.indexOf(activeId)
  const toIndex = getBirdviewOverIndex(over, list)

  return fromIndex < 0 || fromIndex === toIndex
    ? layout
    : { ...layout, [column]: arrayMove(list, fromIndex, toIndex) }
}
